import type { CountryCode } from './types';
import { getCountries, getEventsForCountry, getMinEventsThreshold, getCountryByCode } from './data-source';
import { isPublicHoliday } from './event-filters';
import { SITE_NAME } from './brand';
import { YEAR, getCountryNameEntry, getPhraseEntry, getTemplateStr, getLanguagesForCountry, getLangLabel, getLangLabelEn } from './i18n-helpers';

export interface CountrySlugEntry {
  code: CountryCode;
  lang: string;
  slug: string;
  pageTitle: string;
  pageDescription: string;
  h2Title: string;
  holidaysWord: string;
  /** The old year-suffixed slug for redirect generation */
  legacySlug: string;
}

/** Evergreen slug — no year suffix */
function buildSlug(code: CountryCode, lang: string): string {
  const countryEntry = getCountryNameEntry(code, lang);
  const holidayPhrase = getPhraseEntry('holidays', lang);
  return `${countryEntry.slug}-${holidayPhrase.slug}`;
}

/** Legacy year-suffixed slug — used for redirect generation only */
function buildLegacySlug(code: CountryCode, lang: string): string {
  const countryEntry = getCountryNameEntry(code, lang);
  const holidayPhrase = getPhraseEntry('holidays', lang);
  return `${countryEntry.slug}-${holidayPhrase.slug}-${YEAR}`;
}

function buildEntry(code: CountryCode, lang: string, flag: string, holidayCount: number): CountrySlugEntry {
  const countryEntry = getCountryNameEntry(code, lang);
  const holidayPhrase = getPhraseEntry('holidays', lang);
  const slug = buildSlug(code, lang);

  return {
    code,
    lang,
    slug,
    pageTitle: `${countryEntry.display} ${holidayPhrase.display} ${YEAR} — ${SITE_NAME}`,
    // country_desc reads "<count> public holidays" — so count public holidays,
    // not all country-lens events (festivals/weather). (almanak-564d)
    pageDescription: getTemplateStr('country_desc', lang, { country: countryEntry.display, count: holidayCount, year: YEAR }),
    h2Title: getTemplateStr('country_h2', lang, { country: countryEntry.display, flag, year: YEAR }),
    holidaysWord: holidayPhrase.display,
    legacySlug: buildLegacySlug(code, lang),
  };
}

// --- Registry cache ---

let _slugRegistryCache: Record<string, { code: CountryCode; lang: string }> | null = null;
let _reverseCache: Record<string, string> | null = null;

export function ensureBuilt() {
  if (_slugRegistryCache) return;

  const countries = getCountries();
  const registry: Record<string, { code: CountryCode; lang: string }> = {};
  const reverse: Record<string, string> = {};

  for (const country of countries) {
    if (!country.isDisplayed) continue;

    const allEvents = getEventsForCountry(country.code);
    if (allEvents.length < getMinEventsThreshold('country')) continue;
    const holidayCount = allEvents.filter(isPublicHoliday).length;

    const langs = getLanguagesForCountry(country.code);

    for (const lang of langs) {
      const entry = buildEntry(country.code, lang, country.flag, holidayCount);
      let slug = entry.slug;

      if (registry[slug]) {
        slug = `${slug}-${lang}`;
      }

      registry[slug] = { code: country.code, lang };
      reverse[`${country.code}:${lang}`] = slug;
    }
  }

  // Don't cache an empty result. If the data layer wasn't loaded yet (i.e.
  // a preload hasn't completed for this process), a later call with real
  // data needs to be able to rebuild instead of getting a poisoned empty
  // cache that 404s every slug forever.
  if (Object.keys(registry).length === 0) return;

  _slugRegistryCache = registry;
  _reverseCache = reverse;
}

// --- Public API ---

export function getSlugRegistry(): Record<string, { code: CountryCode; lang: string }> {
  ensureBuilt();
  return _slugRegistryCache ?? {};
}

export function getSlugEntry(slug: string): CountrySlugEntry | null {
  ensureBuilt();
  const match = _slugRegistryCache?.[slug];
  if (!match) return null;

  const country = getCountryByCode(match.code);
  if (!country) return null;

  const holidayCount = getEventsForCountry(match.code).filter(isPublicHoliday).length;
  const entry = buildEntry(match.code, match.lang, country.flag, holidayCount);
  entry.slug = slug;
  return entry;
}

export function getAlternateLanguages(code: CountryCode): Array<{ lang: string; slug: string; label: string }> {
  ensureBuilt();
  const langs = getLanguagesForCountry(code);
  return langs
    .map(lang => ({
      lang,
      slug: _reverseCache?.[`${code}:${lang}`] ?? '',
      label: getLangLabel(lang),
      labelEn: getLangLabelEn(lang),
    }))
    .filter(x => x.slug);
}

export function getSlugForCountry(code: CountryCode, lang: string): string {
  ensureBuilt();
  return _reverseCache?.[`${code}:${lang}`] || _reverseCache?.[`${code}:en`] || '';
}

/** Returns the year-qualified path for a country page: `{evergreen-slug}/{year}` */
export function getSlugForCountryYear(code: CountryCode, lang: string, year: number): string {
  const slug = getSlugForCountry(code, lang);
  return slug ? `${slug}/${year}` : '';
}

/** Returns all legacy year-suffixed slugs mapped to their new evergreen slugs.
 *  Used for 301 redirect generation. */
export function getAllLegacyCountrySlugs(): Array<{ legacySlug: string; evergreenSlug: string }> {
  ensureBuilt();
  const results: Array<{ legacySlug: string; evergreenSlug: string }> = [];
  for (const [slug, { code, lang }] of Object.entries(_slugRegistryCache ?? {})) {
    const entry = getSlugEntry(slug);
    if (entry) {
      results.push({ legacySlug: entry.legacySlug, evergreenSlug: slug });
    }
  }
  return results;
}

/** Check if a slug is a legacy year-suffixed country slug.
 *  Returns the evergreen slug + year if matched, or null. */
export function matchLegacyCountrySlug(slug: string): { evergreenSlug: string; year: string } | null {
  const match = slug.match(/^(.+)-(\d{4})$/);
  if (!match) return null;
  const [, base, year] = match;
  ensureBuilt();
  if (_slugRegistryCache?.[base]) {
    return { evergreenSlug: base, year };
  }
  return null;
}

/** Internal: access the raw slug registry cache for cross-registry functions */
export function _getSlugRegistryCache(): Record<string, { code: CountryCode; lang: string }> {
  ensureBuilt();
  return _slugRegistryCache ?? {};
}
